import { useState } from 'react';
import { UserPlus, X, Loader2, Users, Check, Search } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Checkbox } from '@/components/ui/checkbox';
import { useTeamMembers, useAddTeamMember, useRemoveTeamMember } from '@/hooks/use-teams';
import { useUsers } from '@/hooks/use-users';
import { toast } from '@/components/ui/toaster';
import { getInitials } from '@/lib/utils';
import type { TeamListItem, GlobalRole } from '@/types';

type ApiErrorLike = { response?: { data?: { message?: string } } };

const ROLE_LABELS: Record<GlobalRole, string> = {
  admin: 'Admin',
  supervisor: 'Supervisor',
  user: 'User',
  client: 'Client',
};

interface TeamDetailDialogProps {
  team: TeamListItem;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function TeamDetailDialog({ team, open, onOpenChange }: TeamDetailDialogProps) {
  const { data: members = [], isLoading: membersLoading } = useTeamMembers(team.id);
  const { data: allUsers = [] } = useUsers();
  const addMember = useAddTeamMember();
  const removeMember = useRemoveTeamMember();

  const [selectedUserIds, setSelectedUserIds] = useState<Set<string>>(new Set());
  const [isAdding, setIsAdding] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const memberIds = new Set(members.map((m) => m.userId));
  const availableUsers = allUsers.filter(
    (u) => !memberIds.has(u.id) && u.role !== 'client' && u.isActive
  );

  const filteredAvailableUsers = searchQuery.trim()
    ? availableUsers.filter(
        (u) =>
          u.displayName.toLowerCase().includes(searchQuery.toLowerCase()) ||
          u.email.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : availableUsers;

  const toggleUserSelection = (userId: string) => {
    setSelectedUserIds((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) {
        next.delete(userId);
      } else {
        next.add(userId);
      }
      return next;
    });
  };

  const selectAllFiltered = () => {
    setSelectedUserIds((prev) => {
      const next = new Set(prev);
      filteredAvailableUsers.forEach((u) => next.add(u.id));
      return next;
    });
  };

  const clearSelection = () => {
    setSelectedUserIds(new Set());
  };

  const handleAddSelectedMembers = async () => {
    if (selectedUserIds.size === 0) return;

    setIsAdding(true);
    const userIdsToAdd = Array.from(selectedUserIds);
    let successCount = 0;
    let failCount = 0;

    for (const userId of userIdsToAdd) {
      try {
        await addMember.mutateAsync({ teamId: team.id, userId });
        successCount++;
      } catch {
        failCount++;
      }
    }

    setIsAdding(false);
    setSelectedUserIds(new Set());
    setSearchQuery('');

    if (failCount === 0) {
      toast({ title: `${successCount} member${successCount > 1 ? 's' : ''} added` });
    } else {
      toast({
        title: `Added ${successCount}, failed ${failCount}`,
        variant: failCount > 0 ? 'destructive' : 'default',
      });
    }
  };

  const handleRemoveMember = async (userId: string) => {
    try {
      await removeMember.mutateAsync({ teamId: team.id, userId });
      toast({ title: 'Member removed' });
    } catch (error: unknown) {
      const { response } = error as ApiErrorLike;
      toast({
        title: 'Failed to remove member',
        description: response?.data?.message || 'Please try again',
        variant: 'destructive',
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[85vh] p-0 gap-0 overflow-hidden">
        <DialogHeader className="p-6 pb-4">
          <DialogTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            {team.name}
          </DialogTitle>
          <DialogDescription>
            {team.description || 'Manage team members'}
          </DialogDescription>
        </DialogHeader>

        <div className="px-6 pb-6 space-y-4">
          {/* Add members section */}
          {availableUsers.length > 0 && (
            <div className="border rounded-lg">
              <div className="p-3 border-b bg-muted/50 flex items-center justify-between">
                <span className="text-sm font-medium">
                  Add Members ({availableUsers.length} available)
                </span>
                <div className="flex items-center gap-2">
                  {selectedUserIds.size > 0 && (
                    <>
                      <span className="text-xs text-muted-foreground">
                        {selectedUserIds.size} selected
                      </span>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 text-xs"
                        onClick={clearSelection}
                      >
                        Clear
                      </Button>
                    </>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 text-xs"
                    onClick={selectAllFiltered}
                  >
                    Select all
                  </Button>
                </div>
              </div>
              
              {/* Search input */}
              <div className="p-2 border-b">
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Type to search users..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-8 h-9"
                    autoFocus
                  />
                </div>
                {searchQuery && (
                  <p className="text-xs text-muted-foreground mt-1.5 px-1">
                    Showing {filteredAvailableUsers.length} of {availableUsers.length} users
                  </p>
                )}
              </div>

              {/* User list - fixed height with native scroll */}
              <div className="max-h-[240px] overflow-y-auto">
                <div className="p-2 space-y-0.5">
                  {filteredAvailableUsers.length === 0 ? (
                    <div className="py-8 text-center text-muted-foreground">
                      <Search className="h-6 w-6 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">No users match "{searchQuery}"</p>
                    </div>
                  ) : (
                    filteredAvailableUsers.map((user) => (
                      <label
                        key={user.id}
                        className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted/50 transition-colors cursor-pointer"
                      >
                        <Checkbox
                          checked={selectedUserIds.has(user.id)}
                          onCheckedChange={() => toggleUserSelection(user.id)}
                        />
                        <Avatar className="h-7 w-7 shrink-0">
                          <AvatarImage src={user.avatarUrl || undefined} />
                          <AvatarFallback className="text-xs">
                            {getInitials(user.displayName)}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">
                            {user.displayName}
                          </p>
                          <p className="text-xs text-muted-foreground truncate">
                            {user.email}
                          </p>
                        </div>
                        <Badge variant="outline" className="text-xs shrink-0">
                          {ROLE_LABELS[user.role]}
                        </Badge>
                      </label>
                    ))
                  )}
                </div>
              </div>

              {/* Add button */}
              {selectedUserIds.size > 0 && (
                <div className="p-3 border-t bg-muted/30">
                  <Button
                    onClick={handleAddSelectedMembers}
                    disabled={isAdding}
                    className="w-full gap-2"
                  >
                    {isAdding ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <UserPlus className="h-4 w-4" />
                    )}
                    Add {selectedUserIds.size} member{selectedUserIds.size > 1 ? 's' : ''}
                  </Button>
                </div>
              )}
            </div>
          )}

          {availableUsers.length === 0 && (
            <div className="border rounded-lg p-4 text-center text-muted-foreground">
              <Check className="h-6 w-6 mx-auto mb-2 opacity-50" />
              <p className="text-sm">All eligible users are already members</p>
            </div>
          )}

          {/* Current members list */}
          <div className="border rounded-lg">
            <div className="p-3 border-b bg-muted/50">
              <span className="text-sm font-medium">
                Current Members ({members.length})
              </span>
            </div>
            <div className="max-h-[180px] overflow-y-auto">
              {membersLoading ? (
                <div className="p-4 space-y-3">
                  {[1, 2, 3].map((i) => (
                    <Skeleton key={i} className="h-10 w-full" />
                  ))}
                </div>
              ) : members.length === 0 ? (
                <div className="p-6 text-center text-muted-foreground">
                  <Users className="h-6 w-6 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No members yet</p>
                </div>
              ) : (
                <div className="p-2 space-y-0.5">
                  {members.map((member) => (
                    <div
                      key={member.id}
                      className="flex items-center justify-between p-2 rounded-lg hover:bg-muted/50 transition-colors"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <Avatar className="h-7 w-7 shrink-0">
                          <AvatarImage src={member.user.avatarUrl || undefined} />
                          <AvatarFallback className="text-xs">
                            {getInitials(member.user.displayName)}
                          </AvatarFallback>
                        </Avatar>
                        <div className="min-w-0">
                          <p className="text-sm font-medium truncate">
                            {member.user.displayName}
                          </p>
                          <p className="text-xs text-muted-foreground truncate">
                            {member.user.email}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-xs">
                          {ROLE_LABELS[member.user.role]}
                        </Badge>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6"
                          onClick={() => handleRemoveMember(member.userId)}
                          disabled={removeMember.isPending}
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
